import {
  ImageScaleView,
  ImageScaleViewOpts,
  MovieClip,
  Transition,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { GenericProgressBar } from 'src/game/components/shared/GenericProgressBar';
import { animDuration } from 'src/lib/utils';
import { app } from '../game/app';

export type Match2ScoreProgressbarOpts = {
  superview?: View;
  bgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
  fillStyle?: Omit<ImageScaleViewOpts, 'superview'> & {
    paddingRight?: number;
  };
};

/**
 * Frenzy progress bar
 */
export class Match2ScoreProgressbarNew extends GenericProgressBar {
  // For star FX movieclips that need to display on top of the puzzle grid.
  private animationView: View;

  /**
   * Set progress based on the domain
   */
  public set progress(value: number) {
    this.progressFill.style.width = this.toWidth(value);
    this._progress = value;
  }

  private progressFill: View;
  private paddingRight = 0;
  private stars: Record<
    number,
    {
      base: ImageScaleView;
      animation: MovieClip;
      reached: boolean;
      index: number;
    }
  > = {};

  public set starsPercentage(value: number[]) {
    // Remove all stars.
    Object.values(this.stars).forEach((star) => {
      star.base.removeFromSuperview();
      star.animation.removeFromSuperview();
    });
    this.stars = {};

    // Create new stars.
    const maxStarPercentage = value[value.length - 1];
    const maxX = 280 - this.progressFill.style.x;

    value.forEach((percentage, i) => {
      const ratio = percentage / maxStarPercentage;
      const base = new ImageScaleView({
        superview: this,
        image: 'assets/ui/match2/level/star_frame.png',
        width: 35,
        height: 45,
        x: this.progressFill.style.x + ratio * maxX,
        y: 12,
        centerOnOrigin: true,
      });
      const animation = new MovieClip({
        superview: this.animationView,
        x: this.progressFill.style.x + ratio * maxX,
        y: 25,
        centerOnOrigin: true,
        url: 'assets/ui/match2/movieclips/in_game_stars',
      });
      this.stars[ratio] = { base, animation, reached: false, index: i + 1 };
    });
  }

  constructor(opts: {
    superview?: View;
    bgStyle?: Omit<ImageScaleViewOpts, 'superview'>;
    fillStyle?: Omit<ImageScaleViewOpts, 'superview'> & {
      paddingRight?: number;
    };
  }) {
    const baseOpts = {
      superview: opts.superview,
      x: -150,
      y: -435,
    };

    super({
      ...baseOpts,
      image: 'assets/ui/match2/level/score_bar_bg.png',
      width: 303,
      height: 42,
      scaleMethod: 'contain',
      ...(opts.bgStyle || {}),
    });

    this.animationView = new View(baseOpts);

    this.progressFill = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/level/score_bar_fill.png',
      scaleMethod: '9slice',
      x: 18,
      y: 12,
      width: 70,
      height: 16,
      sourceSlices: {
        horizontal: { left: 7, right: 7 },
        vertical: { top: 0, bottom: 0 },
      },
      ...(opts.fillStyle || {}),
    });
  }

  /**
   * Animate current progress to the given value
   *
   * @param value - Target value
   * @param duration - Animation duration
   */
  public async animateProgress(
    value: number,
    duration: number = animDuration / 4,
    easing: Transition = animate.easeInOut,
  ) {
    this._progress = value;
    await animate(this.progressFill)
      .then({ width: this.toWidth(value) }, duration, easing)
      .toPromise();
    Object.keys(this.stars).forEach((key) => {
      const percentage = Number(key);
      const { base, animation, reached, index } = this.stars[percentage];
      if (reached) return;
      this.stars[percentage].reached = value >= percentage * this.domain[1];
      if (this.stars[percentage].reached) {
        base.updateOpts({
          image: 'assets/ui/match2/level/star.png',
          width: 33,
          height: 33,
          y: 25,
        });

        animation.play('starburst_anim');
        app.sound.play(`sfx-star${index}`);
      }
    });
  }

  /**
   * Convert a value to width considering the domain
   *
   * @param value - Target value
   * @returns
   */
  private toWidth(value: number) {
    // Normalize value.
    const normalizedProgress = this.getNormalizedProgress(value);

    // Convert to width domain.
    const [minWidth, maxWidth] = [10, 266 - this.paddingRight];
    return normalizedProgress
      ? Math.max(minWidth, normalizedProgress * maxWidth)
      : 0;
  }
}
